import { Machine, assign } from '@xstate/compiled';

interface Context {
  limitChanges: {
    [bankId: number]: { limit: string };
  };
}

type Event = { type: 'EDIT_LIMIT'; limit: string; bankId: number };

export const editCurrencyBookExposureLimitMachine = Machine<
  Context,
  Event,
  'editCurrencyBookExposureLimit'
>(
  {
    initial: 'idle',
    context: {
      limitChanges: {},
    },
    states: {
      idle: {
        meta: {
          description: `Awaiting user input - the DB and context are in sync`,
        },
        on: {
          EDIT_LIMIT: {
            actions: 'assignNewLimitToContext',
            target: 'throttlingBeforeErrorCheck',
          },
        },
      },
      throttlingBeforeErrorCheck: {
        meta: {
          description: `Wait 800ms to prevent instant erroring/saving`,
        },
        on: {
          EDIT_LIMIT: {
            actions: 'assignNewLimitToContext',
            target: 'throttlingBeforeErrorCheck',
          },
        },
        after: {
          400: [
            {
              cond: 'aLimitIsNotANumber',
              target: 'errored.aLimitIsNotANumber',
            },
            { cond: 'aLimitIsAbove100', target: 'errored.aLimitIsAbove100' },
            { cond: 'aLimitIsBelow0', target: 'errored.aLimitIsBelow0' },
            { target: 'throttlingBeforeSave' },
          ],
        },
      },
      throttlingBeforeSave: {
        on: {
          EDIT_LIMIT: {
            actions: 'assignNewLimitToContext',
            target: 'throttlingBeforeErrorCheck',
          },
        },
        after: {
          1000: 'saving',
        },
      },
      saving: {
        meta: {
          description: `Saving the changes to the database. Can be interrupted by new inputs`,
        },
        on: {
          EDIT_LIMIT: {
            actions: 'assignNewLimitToContext',
            target: 'throttlingBeforeErrorCheck',
          },
        },
        invoke: {
          src: 'saveLimit',
          onDone: {
            target: 'idle',
            actions: ['onSuccess'],
          },
          onError: {
            target: 'idle',
            actions: ['onError'],
          },
        },
      },
      errored: {
        on: {
          EDIT_LIMIT: {
            actions: 'assignNewLimitToContext',
            target: 'throttlingBeforeErrorCheck',
          },
        },
        states: {
          aLimitIsAbove100: {},
          aLimitIsBelow0: {},
          aLimitIsNotANumber: {},
        },
      },
    },
  },
  {
    actions: {
      assignNewLimitToContext: assign((context, event) => {
        return {
          limitChanges: {
            ...context.limitChanges,
            [event.bankId]: {
              limit: event.limit,
            },
          },
        };
      }),
    },
    guards: {
      aLimitIsNotANumber: (context) => {
        return Object.values(context.limitChanges).some(({ limit }) =>
          isNaN(Number(limit)),
        );
      },
      aLimitIsAbove100: (context) => {
        return Object.values(context.limitChanges).some(
          ({ limit }) => Number(limit) > 100,
        );
      },
      aLimitIsBelow0: (context) => {
        return Object.values(context.limitChanges).some(
          ({ limit }) => Number(limit) < 0,
        );
      },
    },
  },
);
